// Download-this-clip remux: the cloud clip on S3 is an MPEG-TS container (device
// uploads TS every ~15s; spec 17 §0/§4.3). Handing the raw TS bytes to the browser
// as a ".mp4" is a mislabeled/fake mp4 — it only plays in lenient players and other
// apps that expect a real MP4 reject it. So fetch the bytes and transmux TS → MP4 via
// mux.js, then convert mux.js's fragmented output to a **progressive** MP4
// (fmp4ToProgressive) so it plays in QuickTime / Windows Media Player too, matching the
// iOS/Android native download path.
import muxjs from 'mux.js';
import { mediaFetchUrl } from './cloudM3u8';
import { fmp4ToProgressive } from './fmp4ToProgressive';

/** Reject if `p` does not settle within `ms` — so a stuck fetch/transmux can never
 * leave the UI in a permanent "downloading" (greyed button) state. */
export function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timeout after ${ms}ms`)), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/** Transmux one MPEG-TS byte stream into a **progressive** MP4 Blob.
 * mux.js only produces fragmented MP4 (moof-based) — which Chrome/VLC play but QuickTime /
 * Windows Media Player reject — so we run its output through fmp4ToProgressive to get a
 * single-moov progressive file that plays in every default player (matching iOS/Android). */
export function transmuxTsToMp4(ts: Uint8Array): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const transmuxer = new muxjs.mp4.Transmuxer({ keepOriginalTimestamps: true });
      const parts: Uint8Array[] = [];
      let sawInit = false;
      transmuxer.on('data', (seg) => {
        if (!sawInit) {
          parts.push(seg.initSegment.slice());
          sawInit = true;
        }
        parts.push(seg.data.slice());
      });
      transmuxer.on('done', () => {
        if (!sawInit) {
          reject(new Error('transmux produced no output'));
          return;
        }
        try {
          let total = 0;
          for (const p of parts) total += p.length;
          const fmp4 = new Uint8Array(total);
          let off = 0;
          for (const p of parts) {
            fmp4.set(p, off);
            off += p.length;
          }
          const prog = fmp4ToProgressive(fmp4);
          resolve(new Blob([prog.buffer as ArrayBuffer], { type: 'video/mp4' }));
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      });
      transmuxer.push(ts);
      transmuxer.flush();
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/**
 * Fetch a clip's TS bytes (through the same-origin media proxy, like playback does)
 * and return a real MP4 Blob. `refetch` re-signs the url once on 401/403 (expiry).
 */
export async function clipUrlToMp4Blob(
  url: string,
  refetch: () => Promise<string | null>,
  signal?: AbortSignal,
): Promise<Blob> {
  let res = await fetch(mediaFetchUrl(url), { signal });
  if (res.status === 401 || res.status === 403) {
    const fresh = await refetch();
    if (fresh) res = await fetch(mediaFetchUrl(fresh), { signal });
  }
  if (!res.ok) throw new Error(`clip fetch failed: ${res.status}`);
  const ts = new Uint8Array(await res.arrayBuffer());
  return transmuxTsToMp4(ts);
}
